import { useLazyGetAvailableToolsForProjectQuery, useLazyGetToolsQuery } from 'app/services/tools';
import { Tool } from 'app/types/tool';
import { FieldProps, FieldType } from 'components';
import { useEffect, useState } from 'react';
import { parseQrValue } from 'utils/qr/parseQrValue';

type UseGetToolsForProjectProps = {
  projectId?: number;
  unitId?: number;
  required: boolean;
}

const useGetToolsForProject = ({ projectId, unitId, required }: UseGetToolsForProjectProps) => {
  const [ fieldTools, setFieldTools ] = useState<any>([]);
  const [ originalTools, setOriginalTools ] = useState<Tool[]>([]);

  const [ getAvailableTools, { isLoading, isFetching } ] = useLazyGetAvailableToolsForProjectQuery();
  const [ getAllActiveTools, { isLoading: isLoadingAllActiveTools, isFetching: isFetchingAllActiveTools } ] = useLazyGetToolsQuery();

  useEffect(() => {
    const fetchTools = async () => {
      if (!projectId && !unitId) return;
      let tools : Tool[]= [];
      if (projectId) {
        tools = await getAvailableTools({ projectId }).unwrap();
      } else if (unitId) {
        tools = await getAllActiveTools({ unitId, isActive: true }).unwrap();
      }
      setOriginalTools(tools);
      setFieldTools(mapTools(tools));
    };
    fetchTools();
  }, [ projectId, unitId ]);

  const mapTools = (tools: Tool[]) => tools.map((t: Tool) => ({ label: t.Label, value: t.Id, qrCode: t.QrCode }));

  const searchCallback = (value: any) => {
    const valuesToSet = !value ? originalTools : originalTools.filter((tool : Tool) => `${tool.Label}`.toLowerCase().includes(value.toLowerCase()));
    const valuesToSetOptions = mapTools(valuesToSet);
    setFieldTools(valuesToSetOptions);
  };

  const scannerSearchCallback = (scanned: string) => {
    const { barcodeValue } = parseQrValue(scanned);
    const tool = originalTools.find((t: Tool) => t.QrCode === barcodeValue);
    if (tool) return mapTools([ tool ]);
    return [];
  };

  const field: FieldProps =
    {
      name: 'ToolIds',
      type: FieldType.SELECT_SEARCH,
      required: required,
      searchProps: {
        options: fieldTools,
        multiple: true,
        searchCallback: searchCallback,
        setDataCallback: undefined,
        localFilter: true,
      },
      allowEmptySearch: true,
    };

  return {
    field,
    scannerSearchCallback,
    isLoading: isLoading || isLoadingAllActiveTools,
    isFetching: isFetching || isFetchingAllActiveTools,
  };
};

export { useGetToolsForProject };